import React, {useCallback, useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {defaultTheme} from '@stencil/build-theme';

// STYLES
import * as S from './CaptivateBannerGallery.styles';

// ICONS
import HappyGuestCameraIcon from '@stencil/icons/src/HappyGuestCameraIcon/HappyGuestCameraIcon';
import HappyBuildingIcon from '@stencil/icons/src/HappyBuildingIcon/HappyBuildingIcon';

// COMPONENTS
import HappyNewImagePropertyIcon from '@stencil/icons/src/HappyNewImagePropertyIcon/HappyNewImagePropertyIcon';
import Carousel from '../../../common/Carousel/Carousel';

interface Image {
  url: string;
  big: string;
  mediaType: string;
}

interface CaptivateBannerGalleryProps {
  captivateImages: Image[];
  openCaptivateGalleryModal: (type: string) => void;
  hotellierImagesCount: number;
  travellerImagesCount: number;
}

const CaptivateBannerGallery: React.FC<CaptivateBannerGalleryProps> = ({
  captivateImages = [],
  openCaptivateGalleryModal,
  hotellierImagesCount = 0,
  travellerImagesCount = 0,
}) => {
  const handleOpenGalleryModal = useCallback(
    (type: string, e?: React.MouseEvent) => {
      if (e) {
        e?.stopPropagation();
      }
      openCaptivateGalleryModal(type);
    },
    [openCaptivateGalleryModal],
  );

  const renderCarouselItem = useCallback(
    (item: Image, index: number, isLastItem: boolean) => {
      if (item?.mediaType?.toLowerCase() === 'video') return null;

      const conditionalProps = index === 0 ? {itemProp: 'image'} : {};

      if (isLastItem) {
        return (
          <S.WideImageViewAllPhotosOuter key={index}>
            <S.CaptivateGalleryImageViewAll src={item.url} />
            <S.WideImageViewAllPhotos onClick={e => handleOpenGalleryModal('hotellier', e)}>
              <S.ViewAllPhotosText>
                <span>View All</span>
              </S.ViewAllPhotosText>
            </S.WideImageViewAllPhotos>
          </S.WideImageViewAllPhotosOuter>
        );
      }

      return (
        <S.CaptivateGalleryImage
          src={item.url}
          alt="Main gallery"
          key={item.url || index}
          listItemWidth="100%"
          {...conditionalProps}
        />
      );
    },
    [handleOpenGalleryModal],
  );

  const renderOverlayTab = useCallback(
    (
      count: number,
      type: 'hotellier' | 'traveller',
      IconComponent: React.ElementType,
      label: string,
    ) => {
      if (!count) return null;

      return (
        <S.CaptivateGalleryOverlayTab onClick={e => handleOpenGalleryModal(type, e)}>
          <IconComponent fill={defaultTheme.colors.goWhite.base} margin="0 .5rem 0 0" />
          <p>{`${label} (${count > 1000 ? `${Math.floor(count / 1000)}k+` : count})`}</p>
        </S.CaptivateGalleryOverlayTab>
      );
    },
    [handleOpenGalleryModal],
  );

  if (!captivateImages.length) {
    return <HappyNewImagePropertyIcon width="590px" height="324px" margin="0 10px 0 0" />;
  }

  const firstImage = captivateImages?.[0]?.url;

  return (
    <S.CaptivateBannerGallery onClick={e => handleOpenGalleryModal('hotellier', e)}>
      {firstImage && (
        <Helmet>
          <link rel="preload" as="image" href={firstImage} />
        </Helmet>
      )}
      <S.CaptivateBannerOuterContainer>
        <S.WideImagePreviewWrap>
          <S.CaptivateGalleryOverlayTabWrap>
            {renderOverlayTab(
              hotellierImagesCount,
              'hotellier',
              HappyBuildingIcon,
              'Photos by Property',
            )}
            {renderOverlayTab(
              travellerImagesCount,
              'traveller',
              HappyGuestCameraIcon,
              'Photos by Travellers',
            )}
          </S.CaptivateGalleryOverlayTabWrap>
        </S.WideImagePreviewWrap>
        <S.CaptivateBannerGalleryContainer marginBottom={defaultTheme.spacing.base}>
          <Carousel
            preloadImagesOnInteraction
            data={captivateImages}
            renderItem={renderCarouselItem}
          />
        </S.CaptivateBannerGalleryContainer>
      </S.CaptivateBannerOuterContainer>
    </S.CaptivateBannerGallery>
  );
};

export default CaptivateBannerGallery;
